import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
  Divider,
  Stack,
} from "@mui/material";
import TimelineIcon from "@mui/icons-material/Timeline";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import BugReportIcon from "@mui/icons-material/BugReport";
import AssessmentIcon from "@mui/icons-material/Assessment";
import LightbulbIcon from "@mui/icons-material/Lightbulb";

import { COLORS, SCROLLBAR_STYLES, findKey, formatDate } from "./utils/rtmUtils";
import AnalyticsKPICard from "./components/AnalyticsKPICard";
import AnalyticsFilterBar from "./components/AnalyticsFilterBar";
import AnalyticsChart from "./components/AnalyticsChart";

const ExecutionRTMDashboard = ({ data }) => {
  const [filters, setFilters] = useState({
    monthYear: ["All"],
    status: ["All"],
    environment: ["All"],
    build: ["All"],
    executedBy: ["All"],
  });

  const processedData = useMemo(() => {
    if (!data || data.length === 0) return [];
    const sample = data[0];
    const keys = {
      testKey: findKey(sample, "Test Key") || findKey(sample, "Key") || "Test Key",
      summary: findKey(sample, "Summary") || "Summary",
      status: findKey(sample, "Status") || "Status",
      executedBy: findKey(sample, "Executed By") || findKey(sample, "ExecutedBy") || "Executed By",
      executedOn: findKey(sample, "Executed On") || findKey(sample, "Executed Date") || "Executed On",
      defects: findKey(sample, "Defects") || "Defects",
      env: findKey(sample, "Environment") || "Environment",
      build: findKey(sample, "Build") || "Build",
    };

    return data.map((item, index) => {
      const date = item[keys.executedOn] ? new Date(item[keys.executedOn]) : null;
      return {
        id: index,
        testKey: item[keys.testKey] || "Unknown",
        summary: item[keys.summary] || "",
        status: (item[keys.status] || "UNEXECUTED").toUpperCase(),
        executedBy: item[keys.executedBy] || "Unknown",
        executedOn: isNaN(date?.getTime()) ? null : date,
        monthYear: !isNaN(date?.getTime()) ? date.toLocaleString("default", { month: "short", year: "numeric" }) : "Unknown",
        defects: item[keys.defects] || "",
        environment: item[keys.env] || "N/A",
        build: item[keys.build] || "N/A",
      };
    });
  }, [data]);

  const filterOptions = useMemo(() => ({
    monthYear: ["All", ...new Set(processedData.map((d) => d.monthYear))].filter((v) => v !== "Unknown"),
    status: ["All", ...new Set(processedData.map((d) => d.status))],
    environment: ["All", ...new Set(processedData.map((d) => d.environment))],
    build: ["All", ...new Set(processedData.map((d) => d.build))],
    executedBy: ["All", ...new Set(processedData.map((d) => d.executedBy))],
  }), [processedData]);

  const filteredData = useMemo(() => processedData.filter((d) => 
    (filters.monthYear.includes("All") || filters.monthYear.includes(d.monthYear)) &&
    (filters.status.includes("All") || filters.status.includes(d.status)) &&
    (filters.environment.includes("All") || filters.environment.includes(d.environment)) &&
    (filters.build.includes("All") || filters.build.includes(d.build)) &&
    (filters.executedBy.includes("All") || filters.executedBy.includes(d.executedBy))
  ), [processedData, filters]);

  const aggregatedData = useMemo(() => {
    const groups = {};
    filteredData.forEach((d) => {
      if (!groups[d.testKey]) {
        groups[d.testKey] = { testKey: d.testKey, summary: d.summary, executions: [], passCount: 0, failCount: 0, latestDate: null, latestStatus: "UNEXECUTED", defects: new Set() };
      }
      groups[d.testKey].executions.push(d);
      if (d.status === "PASS") groups[d.testKey].passCount++;
      if (d.status === "FAIL") groups[d.testKey].failCount++;
      if (!groups[d.testKey].latestDate || (d.executedOn && d.executedOn > groups[d.testKey].latestDate)) {
        groups[d.testKey].latestDate = d.executedOn;
        groups[d.testKey].latestStatus = d.status;
      }
      if (d.defects) d.defects.split(",").forEach(def => groups[d.testKey].defects.add(def.trim()));
    });
    return Object.values(groups).map(g => ({ ...g, defects: Array.from(g.defects).join(", "), executionCount: g.executions.length }));
  }, [filteredData]);

  const stats = useMemo(() => {
    const totalExec = filteredData.length;
    const uniqueTests = aggregatedData.length;
    const passes = filteredData.filter(d => d.status === "PASS").length;
    const fails = filteredData.filter(d => d.status === "FAIL").length;
    const blocked = filteredData.filter(d => ["BLOCKED", "UNEXECUTED", "NOT RUN"].includes(d.status)).length;
    const totalDefects = new Set();
    filteredData.forEach(d => d.defects && d.defects.split(",").forEach(def => totalDefects.add(def.trim())));

    return {
      totalExecutions: totalExec,
      uniqueTests,
      passRate: totalExec > 0 ? Math.round((passes / totalExec) * 100) : 0,
      blockedRate: totalExec > 0 ? Math.round((blocked / totalExec) * 100) : 0,
      totalDefects: totalDefects.size,
      avgExecPerTest: uniqueTests > 0 ? (totalExec / uniqueTests).toFixed(1) : 0,
    };
  }, [filteredData, aggregatedData]);

  const insights = useMemo(() => {
    const list = [];
    const latestFailures = aggregatedData.filter(g => g.latestStatus === "FAIL").length;
    if (latestFailures > 0) list.push({ text: `${latestFailures} tests are failing in latest run`, type: "error" });
    const flaky = [...aggregatedData].sort((a, b) => b.failCount - a.failCount)[0];
    if (flaky && flaky.failCount > 1) list.push({ text: `Test ${flaky.testKey} is unstable (${flaky.failCount} fails)`, type: "warning" });
    
    const builds = filteredData.reduce((acc, d) => {
      if (!acc[d.build]) acc[d.build] = { total: 0, fail: 0 };
      acc[d.build].total++; if (d.status === "FAIL") acc[d.build].fail++; return acc;
    }, {});
    Object.keys(builds).forEach(b => {
      if (builds[b].fail / builds[b].total > 0.3) list.push({ text: `Build ${b} has high failure rate (${Math.round((builds[b].fail / builds[b].total) * 100)}%)`, type: "error" });
    });
    return list;
  }, [filteredData, aggregatedData]);

  const handleFilterChange = (name, value) => setFilters(prev => ({ ...prev, [name]: value }));

  // --- Chart Data Memos ---
  
  const statusData = useMemo(() => {
    const counts = filteredData.reduce((acc, d) => { acc[d.status] = (acc[d.status] || 0) + 1; return acc; }, {});
    return { labels: Object.keys(counts), datasets: [{ data: Object.values(counts), backgroundColor: COLORS.PALETTE }] };
  }, [filteredData]);

  const trendData = useMemo(() => {
    const counts = filteredData.reduce((acc, d) => {
      if (d.status.startsWith("PASS")) acc.pass++; else if (d.status.startsWith("FAIL")) acc.fail++;
      else if (d.status.includes("BLOCK")) acc.blocked++; else acc.notRun++; return acc;
    }, { pass: 0, fail: 0, blocked: 0, notRun: 0 });
    return {
      labels: ["Pass", "Fail", "Blocked", "Unexecuted"],
      datasets: [{ label: "Executions", data: [counts.pass, counts.fail, counts.blocked, counts.notRun], borderColor: COLORS.PRIMARY, backgroundColor: `${COLORS.PRIMARY}15`, fill: true, tension: 0.4, pointBackgroundColor: [COLORS.PASS, COLORS.FAIL, COLORS.BLOCKED, COLORS.UNEXECUTED] }]
    };
  }, [filteredData]);

  const stabilityData = useMemo(() => {
    const topFlaky = [...aggregatedData].sort((a, b) => b.failCount - a.failCount).slice(0, 10);
    return { labels: topFlaky.map(d => d.testKey), datasets: [{ label: "Fail Count", data: topFlaky.map(d => d.failCount), backgroundColor: COLORS.FAIL }] };
  }, [aggregatedData]);

  const defectMapData = useMemo(() => {
    const defectMap = {};
    filteredData.forEach(d => d.defects && d.defects.split(",").forEach(def => { const t = def.trim(); defectMap[t] = (defectMap[t] || 0) + 1; }));
    const labels = Object.keys(defectMap).sort((a, b) => defectMap[b] - defectMap[a]).slice(0, 10);
    return { labels, datasets: [{ label: "Failed Executions", data: labels.map(l => defectMap[l]), backgroundColor: COLORS.SECONDARY }] };
  }, [filteredData]);

  const envData = useMemo(() => {
    const envs = filteredData.reduce((acc, d) => {
      if (!acc[d.environment]) acc[d.environment] = { pass: 0, fail: 0 };
      if (d.status === "PASS") acc[d.environment].pass++; if (d.status === "FAIL") acc[d.environment].fail++; return acc;
    }, {});
    const labels = Object.keys(envs);
    return { labels, datasets: [{ label: "Pass", data: labels.map(l => envs[l].pass), backgroundColor: COLORS.PASS }, { label: "Fail", data: labels.map(l => envs[l].fail), backgroundColor: COLORS.FAIL }] };
  }, [filteredData]);

  const buildQualityData = useMemo(() => {
    const builds = filteredData.reduce((acc, d) => { if (!acc[d.build]) acc[d.build] = { total: 0, pass: 0 }; acc[d.build].total++; if (d.status.startsWith("PASS")) acc[d.build].pass++; return acc; }, {});
    const labels = Object.keys(builds);
    return { labels, datasets: [{ label: "Pass Rate %", data: labels.map(l => Math.round((builds[l].pass / builds[l].total) * 100)), borderColor: COLORS.SECONDARY, backgroundColor: `${COLORS.SECONDARY}20`, fill: true, tension: 0.4 }] };
  }, [filteredData]);

  return (
    <Box sx={{ p: 1, bgcolor: "#f8f9fa", minHeight: "100vh" }}>
      <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 3, bgcolor: "white", boxShadow: "0 10px 30px 0 rgba(0,0,0,0.05)" }}>
        <AnalyticsFilterBar filters={filters} options={filterOptions} onFilterChange={handleFilterChange} />
      </Paper>

      <Grid container spacing={2} mb={4}>
        <Grid item xs={12} sm={6} md={2}><AnalyticsKPICard title="Total Exec" value={stats.totalExecutions} icon={<TimelineIcon />} color={COLORS.PRIMARY} /></Grid>
        <Grid item xs={12} sm={6} md={2}><AnalyticsKPICard title="Unique Tests" value={stats.uniqueTests} icon={<AssignmentIcon />} color={COLORS.SECONDARY} /></Grid>
        <Grid item xs={12} sm={6} md={2}><AnalyticsKPICard title="Pass Rate" value={`${stats.passRate}%`} icon={<CheckCircleOutlineIcon />} color={COLORS.DONE} /></Grid>
        <Grid item xs={12} sm={6} md={2}><AnalyticsKPICard title="Blocked" value={`${stats.blockedRate}%`} icon={<TrendingUpIcon />} color={COLORS.TO_DO} /></Grid>
        <Grid item xs={12} sm={6} md={2}><AnalyticsKPICard title="Total Defects" value={stats.totalDefects} icon={<BugReportIcon />} color={COLORS.FAIL} /></Grid>
        <Grid item xs={12} sm={6} md={2}><AnalyticsKPICard title="Avg Exec/Test" value={stats.avgExecPerTest} icon={<AssessmentIcon />} color={COLORS.PRIMARY} /></Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12}><Paper sx={{ p: 3, borderRadius: 4 }}><Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Execution Trend</Typography><AnalyticsChart type="line" data={trendData} height={350} /></Paper></Grid>
        <Grid item xs={12} md={8}><Paper sx={{ p: 3, borderRadius: 4 }}><Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Status Distribution</Typography><AnalyticsChart type="doughnut" data={statusData} options={{ cutoutPercentage: 70 }} height={350} /></Paper></Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, borderRadius: 4, height: "100%", bgcolor: "white" }}>
            <Box display="flex" alignItems="center" mb={2}><LightbulbIcon sx={{ color: "#fbc02d", mr: 1 }} /><Typography variant="h6" sx={{ fontWeight: 700 }}>Smart Insights</Typography></Box>
            <Stack spacing={2}>
              {insights.map((insight, i) => (
                <Box key={i} sx={{ p: 2, borderRadius: 2, bgcolor: insight.type === "error" ? "#fff5f5" : "#fff9f0", borderLeft: `4px solid ${insight.type === "error" ? COLORS.FAIL : COLORS.TO_DO}` }}><Typography variant="body2" sx={{ fontWeight: 600 }}>{insight.text}</Typography></Box>
              ))}
            </Stack>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}><Paper sx={{ p: 3, borderRadius: 4 }}><Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Test Stability</Typography><AnalyticsChart type="bar" data={stabilityData} height={350} /></Paper></Grid>
        <Grid item xs={12} md={6}><Paper sx={{ p: 3, borderRadius: 4 }}><Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Defect Mapping</Typography><AnalyticsChart type="bar" data={defectMapData} height={350} /></Paper></Grid>
        <Grid item xs={12} md={6}><Paper sx={{ p: 3, borderRadius: 4 }}><Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Environment Results</Typography><AnalyticsChart type="bar" data={envData} height={350} /></Paper></Grid>
        <Grid item xs={12} md={6}><Paper sx={{ p: 3, borderRadius: 4 }}><Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Build Quality</Typography><AnalyticsChart type="line" data={buildQualityData} height={350} /></Paper></Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} lg={8}>
          <Paper sx={{ p: 3, borderRadius: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Latest Executions</Typography>
            <TableContainer sx={{ maxHeight: 500, ...SCROLLBAR_STYLES }}>
              <Table stickyHeader size="small">
                <TableHead><TableRow>{["Test Key", "Summary", "Status", "Last Executed", "Fails", "Passes", "Defects"].map(h => <TableCell key={h} sx={{ fontWeight: 700, bgcolor: "#f8f9fa" }}>{h}</TableCell>)}</TableRow></TableHead>
                <TableBody>
                  {aggregatedData.map((row, i) => (
                    <TableRow key={i} hover>
                      <TableCell><Chip label={row.testKey} size="small" variant="outlined" sx={{ fontWeight: 700, color: COLORS.PRIMARY }} /></TableCell>
                      <TableCell sx={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}><Tooltip title={row.summary}><span>{row.summary}</span></Tooltip></TableCell>
                      <TableCell><Chip label={row.latestStatus} size="small" sx={{ bgcolor: `${COLORS[row.latestStatus] || COLORS.UNEXECUTED}20`, color: COLORS[row.latestStatus] || COLORS.UNEXECUTED, fontWeight: 700 }} /></TableCell>
                      <TableCell>{formatDate(row.latestDate)}</TableCell>
                      <TableCell align="center" sx={{ color: "error.main", fontWeight: 700 }}>{row.failCount}</TableCell>
                      <TableCell align="center" sx={{ color: "success.main", fontWeight: 700 }}>{row.passCount}</TableCell>
                      <TableCell>{row.defects ? row.defects.split(",").map((d, j) => <Chip key={j} label={d.trim()} size="small" color="error" variant="outlined" sx={{ m: 0.2, fontSize: "0.6rem" }} />) : "-"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
        <Grid item xs={12} lg={4}>
          <Paper sx={{ p: 3, borderRadius: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Defect Leakage</Typography>
            <TableContainer sx={{ maxHeight: 500, ...SCROLLBAR_STYLES }}>
              <Table stickyHeader size="small">
                <TableHead><TableRow><TableCell sx={{ fontWeight: 700, bgcolor: "#f8f9fa" }}>Defect ID</TableCell><TableCell sx={{ fontWeight: 700, bgcolor: "#f8f9fa" }}>Linked Test</TableCell><TableCell sx={{ fontWeight: 700, bgcolor: "#f8f9fa" }}>Status</TableCell></TableRow></TableHead>
                <TableBody>
                  {filteredData.filter(d => d.defects).flatMap(d => d.defects.split(",").map(def => ({ defectId: def.trim(), testKey: d.testKey, status: d.status }))).map((row, i) => (
                    <TableRow key={i} hover><TableCell sx={{ fontWeight: 700, color: COLORS.FAIL }}>{row.defectId}</TableCell><TableCell>{row.testKey}</TableCell><TableCell><Chip label={row.status} size="small" variant="outlined" /></TableCell></TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ExecutionRTMDashboard;
